import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { BadgeComponent } from '@vendasta/galaxy/badge';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SessionProposedActionDataStatus } from '@vendasta/protos/ai_assistants/v1alpha1';
import { getSessionEvent$ } from '@vendasta/protos/ai_assistants/v1alpha1/session-service';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { ProposedActionApproval, ProposedActionRejection } from './ai-proposed-action.types';
import { AiProposedActionArgumentsDialogComponent } from './ai-proposed-action-arguments-dialog.component';
import { NamespaceFromTypeAndId } from '../../core/services/ai-assistant-utils';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';

@Component({
  selector: 'ai-proposed-action',
  imports: [AiAssistantI18nModule, BadgeComponent, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ai-proposed-action.component.html',
  styleUrls: ['./ai-proposed-action.component.scss'],
})
export class AiProposedActionComponent {
  readonly namespaceType = input.required<string>();
  readonly namespaceId = input.required<string>();
  readonly sessionId = input.required<string>();
  readonly eventId = input.required<string>();
  readonly superseded = input<boolean>(false);

  readonly approved = output<ProposedActionApproval>();
  readonly rejected = output<ProposedActionRejection>();
  readonly changeRequested = output<void>();

  protected readonly changeSelected = signal(false);
  protected readonly actionInProgress = signal(false);

  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly refreshTrigger = signal(0);

  protected readonly data = toSignal(
    combineLatest([
      toObservable(this.namespaceId),
      toObservable(this.namespaceType),
      toObservable(this.sessionId),
      toObservable(this.eventId),
      toObservable(this.refreshTrigger),
    ]).pipe(
      switchMap(([namespaceId, namespaceType, sessionId, eventId]) => {
        const namespace = NamespaceFromTypeAndId(namespaceType, namespaceId);
        return getSessionEvent$({ namespace, sessionId, eventId }).pipe(
          map((response) => response.event?.proposedAction ?? null),
          catchError(() => {
            this.snackbarService.openErrorSnack('AI_ASSISTANT.PROPOSED_ACTION.FETCH_ERROR');
            return of(null);
          }),
        );
      }),
    ),
    { initialValue: null },
  );

  protected readonly isPending = computed(
    () => this.data()?.status === SessionProposedActionDataStatus.PENDING && !this.superseded(),
  );
  protected readonly isApproved = computed(() => this.data()?.status === SessionProposedActionDataStatus.APPROVED);
  protected readonly isRejected = computed(() => this.data()?.status === SessionProposedActionDataStatus.REJECTED);
  protected readonly isSuperseded = computed(
    () =>
      this.data()?.status === SessionProposedActionDataStatus.SUPERSEDED ||
      (this.data()?.status === SessionProposedActionDataStatus.PENDING && this.superseded()),
  );

  protected readonly statusIcon = computed(() => {
    if (this.isApproved()) return 'check';
    if (this.isRejected() || this.isSuperseded()) return 'close';
    return null;
  });

  protected readonly statusLabel = computed(() => {
    if (this.isApproved()) return 'AI_ASSISTANT.PROPOSED_ACTION.APPROVED';
    if (this.isRejected() || this.isSuperseded()) return 'AI_ASSISTANT.PROPOSED_ACTION.DECLINED';
    return null;
  });

  protected readonly formattedArguments = computed(() => {
    const args = this.data()?.arguments;
    if (!args) return '';
    try {
      return JSON.stringify(JSON.parse(args), null, 2);
    } catch {
      return args;
    }
  });

  constructor() {
    effect(() => {
      if (!this.isPending()) {
        this.changeSelected.set(false);
      }
    });
  }

  reload(): void {
    this.refreshTrigger.update((n) => n + 1);
  }

  resetActionInProgress(): void {
    this.actionInProgress.set(false);
  }

  protected approveFeedback(): string {
    return this.data()?.approveText || this.translate.instant('AI_ASSISTANT.PROPOSED_ACTION.YES');
  }

  protected onApprove(): void {
    this.actionInProgress.set(true);
    this.approved.emit({
      sessionId: this.sessionId(),
      eventId: this.eventId(),
      feedback: this.approveFeedback(),
    });
  }

  protected onReject(): void {
    this.emitRejection(this.translate.instant('AI_ASSISTANT.PROPOSED_ACTION.NO'));
  }

  protected onChangeSomething(): void {
    this.changeSelected.set(true);
    this.changeRequested.emit();
  }

  protected openArguments(): void {
    const data = this.data();
    if (!data) return;
    this.dialog.open(AiProposedActionArgumentsDialogComponent, {
      data: {
        functionId: data.functionId,
        arguments: this.formattedArguments(),
      },
    });
  }

  private emitRejection(feedback: string): void {
    this.actionInProgress.set(true);
    this.rejected.emit({
      sessionId: this.sessionId(),
      eventId: this.eventId(),
      feedback,
    });
  }
}
